import React from 'react';
import { X, Minus, Plus, ShoppingBag, Trash2, ArrowRight, Loader2 } from 'lucide-react';

const CartDrawer = ({ 
  isOpen, 
  onClose, 
  cart, 
  onUpdateQuantity, 
  onRemoveItem, 
  onCheckout,
  loading 
}) => {
  if (!isOpen) return null;

  const lines = cart?.lines || [];
  const totalAmount = parseFloat(cart?.totalAmount || 0).toFixed(2);
  const currency = cart?.currency || 'USD';

  return (
    <div className="fixed inset-0 z-[70]">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />
      
      {/* Drawer */}
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-stone-100">
          <div className="flex items-center gap-3">
            <ShoppingBag className="w-5 h-5 text-stone-700" />
            <h2 className="text-lg font-semibold text-stone-800">Your Cart</h2>
            <span className="text-sm text-stone-500">
              ({lines.length} {lines.length === 1 ? 'item' : 'items'})
            </span>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-stone-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-stone-600" />
          </button>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-stone-400 animate-spin" />
            </div>
          ) : lines.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <ShoppingBag className="w-16 h-16 text-stone-200 mb-4" />
              <p className="text-stone-500 mb-2">Your cart is empty</p>
              <p className="text-sm text-stone-400">Add some products to get started</p>
            </div>
          ) : (
                <div className="space-y-4">
              {lines.map((item) => (
                <div 
                  key={item.id}
                  className="flex gap-4 p-4 bg-stone-50 rounded-xl"
                >
                  {/* Product Image */}
                  <div className="w-20 h-20 bg-white rounded-lg overflow-hidden flex-shrink-0">
                    {item.image ? (
                      <img 
                        src={item.image} 
                        alt={item.productTitle}
                        className="w-full h-full object-contain p-2"
                      />
                    ) : (
                      <div className="w-full h-full bg-stone-100 flex items-center justify-center">
                        <ShoppingBag className="w-6 h-6 text-stone-300" />
                      </div>
                    )}
                  </div>

                  {/* Product Details */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-medium text-stone-800 truncate">
                      {item.productTitle}
                    </h3>
                    {item.variantTitle && item.variantTitle !== 'Default Title' && (
                      <p className="text-xs text-stone-400 mt-0.5">{item.variantTitle}</p>
                    )}
                    {/* Selling Plan Badge */}
                    {item.sellingPlan && (
                      <p className="text-xs text-emerald-600 font-medium mt-0.5">
                        {item.sellingPlan.name}
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-sm font-semibold text-stone-900">
                        ${parseFloat(item.price).toFixed(2)}
                      </p>
                      {item.sellingPlan && item.originalPrice && parseFloat(item.originalPrice) > parseFloat(item.price) && (
                        <p className="text-xs text-stone-400 line-through">
                          ${parseFloat(item.originalPrice).toFixed(2)}
                        </p>
                      )}
                    </div>

                    {/* Quantity Controls */}
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => onUpdateQuantity(item.id, Math.max(0, item.quantity - 1))}
                          className="w-7 h-7 rounded-full bg-white border border-stone-200 flex items-center justify-center hover:bg-stone-100 transition-colors"
                          disabled={loading}
                        >
                          <Minus className="w-3 h-3 text-stone-600" />
                        </button>
                        <span className="text-sm font-medium text-stone-700 w-6 text-center">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
                          className="w-7 h-7 rounded-full bg-white border border-stone-200 flex items-center justify-center hover:bg-stone-100 transition-colors"
                          disabled={loading}
                        >
                          <Plus className="w-3 h-3 text-stone-600" />
                        </button>
                      </div>
                      
                      <button
                        onClick={() => onRemoveItem(item.id)}
                        className="p-2 text-stone-400 hover:text-rose-500 transition-colors"
                        disabled={loading}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {lines.length > 0 && (
          <div className="p-6 border-t border-stone-100 bg-white">
            {/* Subtotal */}
            <div className="flex items-center justify-between mb-4">
              <span className="text-stone-600">Subtotal</span>
              <span className="text-xl font-semibold text-stone-900">
                ${totalAmount} {currency}
              </span>
            </div>
            
            <p className="text-xs text-stone-400 mb-4">
              Shipping and taxes calculated at checkout
            </p>

            {/* Checkout Button */}
            <button
              onClick={onCheckout}
              disabled={loading}
              className="w-full py-4 bg-stone-900 hover:bg-stone-800 text-white font-medium rounded-xl flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  Proceed to Checkout
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartDrawer;
